import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import httpClient from '../../services/httpClient';
import { serviceService } from './serviceService';
import { topUpCardService } from './topUpCardService';

type Category = {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  serviceCategory?: string | null;
};

const fmtPrice = (p: number) => new Intl.NumberFormat('ru-RU').format(p);
type CatalogCard = { id: number; name: string; picture: string; price: number; href: string; subtitle?: string };

const CategoryPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [category, setCategory] = useState<Category | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [products, setProducts] = useState<CatalogCard[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        // Категорию резолвим по slug из публичного списка.
        const { data } = await httpClient.get<Category[]>('/api/Categories');
        const list = Array.isArray(data) ? data : [];
        const cat = list.find(c => c.slug === slug) ?? null;
        if (cancelled) return;

        if (!cat) {
          setNotFound(true);
          setCategory(null);
          setProducts([]);
          return;
        }
        setCategory(cat);

        // Фильтруем товары по ServiceCategory категории.
        let items: CatalogCard[] = [];
        if (cat.slug === 'gift-cards') {
          const cards = await topUpCardService.list();
          items = cards.flatMap(card => card.denominations.filter(d => d.isAvailable).map(d => ({
            id: d.id ?? 0,
            name: `${d.faceValue.toLocaleString('ru-RU')} ${d.currency}`,
            picture: d.picture || card.picture,
            price: d.price,
            href: `/top-up-card/${card.id}?denomination=${d.id}`,
            subtitle: d.region === 'Turkey' ? '🇹🇷 Турция' : '🇮🇳 Индия',
          })));
        } else if (cat.serviceCategory) {
          const services = await serviceService.list(cat.serviceCategory);
          items = services.map(service => ({ ...service, href: `/service/${service.id}` }));
        }
        if (!cancelled) setProducts(items);
      } catch {
        if (!cancelled) { setProducts([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [slug]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-background px-4 py-6 sm:py-8">
      <div className="mx-auto max-w-[90rem]">
        <div className="mb-6">
          <Link to="/" className="text-sm text-foreground/50 hover:text-foreground">← На главную</Link>
          <h1 className="mt-3 text-4xl font-bold tracking-tight text-foreground sm:text-6xl">
            {category?.name ?? (notFound ? 'Категория не найдена' : 'Категория')}
          </h1>
          {category?.description && (
            <p className="mt-3 max-w-3xl text-base font-normal text-foreground/50 sm:text-lg">
              {category.description}
            </p>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-[5/6] animate-pulse rounded-[20px] border border-foreground/10 bg-foreground/5" />
            ))}
          </div>
        ) : notFound ? (
          <p className="text-foreground/50">Такой категории нет.</p>
        ) : products.length === 0 ? (
          <p className="text-foreground/50">В этой категории пока нет товаров.</p>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:gap-[18px] md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {products.map(p => (
              <Link key={`${p.href}-${p.id}`} to={p.href} className="block h-full">
                <article className="group flex h-full min-h-0 flex-col overflow-hidden rounded-[20px] border border-foreground/10 bg-[hsl(var(--card-tinted))] transition-all duration-300 hover:-translate-y-1 hover:border-foreground/20 sm:rounded-[28px]">
                  <div className="relative aspect-[5/6] w-full overflow-hidden">
                    <img
                      src={p.picture}
                      alt={p.name}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-2 pb-3 text-left sm:p-4">
                    {p.subtitle && <div className="mb-1 text-xs text-foreground/55">{p.subtitle}</div>}
                    <div className="text-xl font-semibold text-foreground">{fmtPrice(p.price)} ₽</div>
                    <h3 className="mt-1 line-clamp-2 min-h-[2.6em] text-base font-normal leading-[1.3] text-foreground sm:mt-3 sm:text-[18px]">
                      {p.name}
                    </h3>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
